import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type GridFilterModel, type GridRowId } from '@mui/x-data-grid-pro'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom-v5-compat'

import { useGetNotificationUnreadCountQuery, useGetNotificationsQuery } from 'api/notification-center/notificationCenter'
import NavbarWrapper from 'components/common/NavbarWrapper'
import ReorderIcon from 'components/common/ReorderIcon'
import SideNavLayout from 'components/common/side-nav/SideNavLayout'
import NotificationCenterDrawer from 'components/notifications/notification-center/NotificationCenterDrawer'
import { buildNotificationListParams } from 'components/notifications/notification-center/notificationCenterFilters'
import NotificationCenterGrid from 'components/notifications/notification-center/NotificationCenterGrid'
import {
  NOTIFICATION_FOCUS_PARAM,
  NOTIFICATION_VIEW,
  NOTIFICATION_VIEW_PARAM,
  viewForKey,
} from 'components/notifications/notification-center/notificationCenterViews'
import { useNotificationLiveSet } from 'components/notifications/notification-center/useNotificationLiveSet'
import { useNotificationReadState } from 'components/notifications/notification-center/useNotificationReadState'
import { openDrawer, selectIsNotificationDrawerOpen } from 'slices/notification-center/notificationDrawerSlice'
import T from 'T'

import type { Notification } from 'types/modules/notification-center/notification'

const SEARCH_DEBOUNCE_MS = 400

const NotificationCenterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  // The input stays responsive; only the value the query is built from waits.
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([])
  /** The row a bell or toast click asked for; the grid scrolls to it once it is in the rows. */
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(undefined)

  const dispatch = useDispatch()
  const isDrawerOpen = useSelector(selectIsNotificationDrawerOpen)

  const liveSet = useNotificationLiveSet()
  const activeViewKey = searchParams.get(NOTIFICATION_VIEW_PARAM) ?? NOTIFICATION_VIEW.ALL
  const activeView = viewForKey(activeViewKey, liveSet.views)

  const listParams = useMemo(
    () => buildNotificationListParams(activeViewKey, searchTerm, filterModel, liveSet.areas),
    [activeViewKey, searchTerm, filterModel, liveSet.areas]
  )

  const { data, isLoading, isFetching } = useGetNotificationsQuery(listParams)
  const { data: serverUnreadCount } = useGetNotificationUnreadCountQuery()
  const { markEverythingRead, isMarkingEverythingRead } = useNotificationReadState()

  // Every dimension is on the query now, so the response is already the view.
  const rows = useMemo<Notification[]>(() => data?.notifications ?? [], [data])

  const totalItems = data?.totalItems ?? rows.length

  const unreadCount = serverUnreadCount ?? 0

  /**
   * Arriving from the bell or a toast with a notification named: open that row and hand the id to the grid
   * to scroll to, then drop the param. Keeping the param would re-expand the row every render, so
   * collapsing it would never stick — which is why the id moves into state rather than being read from the
   * URL where it is needed.
   */
  useEffect(() => {
    const fromUrl = searchParams.get(NOTIFICATION_FOCUS_PARAM)
    if (!fromUrl) return
    setFocusedId(fromUrl)
    setExpandedRowIds([fromUrl])
    const remaining = new URLSearchParams(searchParams)
    remaining.delete(NOTIFICATION_FOCUS_PARAM)
    setSearchParams(remaining, { replace: true })
  }, [searchParams, setSearchParams])

  const debouncedSetSearchTerm = useMemo(() => debounce((value: string) => setSearchTerm(value), SEARCH_DEBOUNCE_MS), [])

  useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm])

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value)
      debouncedSetSearchTerm(event.target.value)
    },
    [debouncedSetSearchTerm]
  )

  /** Clearing is deliberate, so it takes effect at once rather than waiting out the debounce. */
  const handleClearSearch = useCallback(() => {
    debouncedSetSearchTerm.cancel()
    setQuery('')
    setSearchTerm('')
  }, [debouncedSetSearchTerm])

  // Opening a row is reading it, not answering it: the read state is the row's own toggle to set.
  const handleExpandedRowIdsChange = useCallback((ids: GridRowId[]) => setExpandedRowIds(ids), [])

  /** The rail's items are links now, so they own the URL; this only drops state the old view left behind. */
  const handleSelectView = useCallback(() => setExpandedRowIds([]), [])

  // The persistent drawer's own chevron is the only way to close it, and a persistent drawer never
  // fires `onOpen` — so without this the rail is gone until a resize or a reload.
  const handleOpenDrawer = useCallback(() => dispatch(openDrawer()), [dispatch])

  return (
    <NavbarWrapper innerSx={{ overflow: 'hidden' }} sx={{ backgroundColor: 'background.paper' }}>
      <NotificationCenterDrawer activeViewKey={activeViewKey} unreadCount={unreadCount} onSelectView={handleSelectView} />

      <SideNavLayout open={isDrawerOpen} overflow="hidden">
        <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2} sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Stack alignItems="flex-start" direction="row" spacing={1} sx={{ minWidth: 0 }}>
            {!isDrawerOpen && <ReorderIcon aria-label={T.SHOW_NOTIFICATION_VIEWS} onClick={handleOpenDrawer} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4">{`${activeView.title} (${totalItems})`}</Typography>
              <Typography color="text.lightBrown" variant="body1">
                {T.NOTIFICATION_CENTER_DESCRIPTION}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              disabled={unreadCount === 0}
              loading={isMarkingEverythingRead}
              size="small"
              startIcon={<DoneAllOutlinedIcon />}
              onClick={markEverythingRead}
            >
              {T.MARK_ALL_AS_READ}
            </Button>
          </Stack>
        </Stack>
        <Divider />

        <Box sx={{ flex: 1, minHeight: 0, px: 3, pb: 3 }}>
          <NotificationCenterGrid
            activeViewKey={activeViewKey}
            expandedRowIds={expandedRowIds}
            filterModel={filterModel}
            focusedId={focusedId}
            isLoading={isLoading || isFetching}
            notifications={rows}
            query={query}
            onExpandedRowIdsChange={handleExpandedRowIdsChange}
            onFilterModelChange={setFilterModel}
            onQueryChange={handleSearchChange}
            onQueryClear={handleClearSearch}
          />
        </Box>
      </SideNavLayout>
    </NavbarWrapper>
  )
}

export default NotificationCenterPage
